import { Component, OnInit, input, inject } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEye } from '@fortawesome/pro-solid-svg-icons';
import { ActivityCountsComponent, Count } from '../activity-counts/activity-counts.component';
import { CountsBarChartComponent } from '../counts-bar-chart/counts-bar-chart.component';
import { DateTime } from 'luxon';
import { ActionActivityDialogComponent, ActionActivityDialogData } from '../dialogs/action-activity-dialog/action-activity-dialog.component';
import { ReportingSumRequest } from '@r1-compass-library/models/reporting/reporting-request.model';
import { CountsType, ReportingDataStore } from '../store/reporting-data.store';
import { ChartResultElement } from '@r1-compass-library/models/reporting/chart-data.model';
import { AggregateResponse } from '@r1-compass-library/models/reporting/aggregate-response.model';
import { MatButtonModule } from '@angular/material/button';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { PlayResultDetail } from '@r1-compass-library/models';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
    selector: 'r1-compass-app-play-activity',
    imports: [FontAwesomeModule, MatDialogModule, MatButtonModule, ActivityCountsComponent, CountsBarChartComponent],
    templateUrl: './play-activity.component.html',
    styleUrl: './play-activity.component.scss',
})
export class PlayActivityComponent implements OnInit {
    readonly #store = inject(ReportingDataStore);
    readonly #userStore = inject(GlobalLoggedInUserStore);
    readonly #dialog = inject(MatDialog);

    selectedDivisionIds = input.required<number[]>();
    selectedDivisionIdsObersvable = toObservable(this.selectedDivisionIds);

    private oneDayInSeconds = 86400;

    readonly timeRangeInDays = 7; // includes the current day
    readonly today: DateTime = DateTime.now().startOf('day');
    readonly dateDisplayFormat = 'MMM d';
    colors: string[] = ['#93d500', '#1174b6', '#ff9933', '#f3b88c'];
    chartRows: Map<number, string> = new Map();
    chartData: ChartResultElement[] = [];
    divisionId?: number = this.#userStore.selectedIamDivision()?.id;
    totalCounts: Count[] = [
        {
            label: 'Records Created',
            order: 0,
            value: 0,
        },
        {
            label: 'Records Updated',
            order: 1,
            value: 0,
        },
        {
            label: 'Records Failed',
            order: 2,
            value: 0,
        },
        {
            label: 'Records Rejected',
            order: 3,
            value: 0,
        },
    ];

    constructor() {
        const library = inject(FaIconLibrary);

        library.addIcons(faEye);
    }

    ngOnInit() {
        this.setChartLabels();
        this.initChartData();

        this.selectedDivisionIdsObersvable.subscribe(divs => {
            if (divs.length) {
                this.loadCountsForLast24Hours(divs)
                this.loadSums(divs);
            } else {
                this.#store.resetState()
            }
        })

        this.#store.recommendationsCreated$.subscribe(created => {
            this.setChartData(created, 'Created');
            this.totalCounts.find(total => total.label === 'Records Created')!.value = created.reduce(
                (sum, current) => sum + current.aggregate,
                0
            );
        });

        this.#store.recommendationsUpdated$.subscribe(updated => {
            this.setChartData(updated, 'Updated');
            this.totalCounts.find(total => total.label === 'Records Updated')!.value = updated.reduce(
                (sum, current) => sum + current.aggregate,
                0
            );
        });

        this.#store.recommendationsFailed$.subscribe(failed => {
            this.setChartData(failed, 'Failed');
            this.totalCounts.find(total => total.label === 'Records Failed')!.value = failed.reduce(
                (sum, current) => sum + current.aggregate,
                0
            );
        });

        this.#store.recommendationsRejected$.subscribe(rejected => {
            this.setChartData(rejected, 'Rejected');
            this.totalCounts.find(total => total.label === 'Records Rejected')!.value = rejected.reduce(
                (sum, current) => sum + current.aggregate,
                0
            );
        });
    }

    private setChartData(aggregateData: AggregateResponse[], countType: 'Created' | 'Updated' | 'Failed' | 'Rejected') {
        const normalizedAggregateResponseArray = this.normalizeAggregateResponseArray(aggregateData);

        const clone = structuredClone(this.chartData);
        this.chartRows.forEach((value, key) => {
            // Note about the clone: If we change the value of some nested property in chartData, then change-detection doesn't fire
            // and the updated chartData is not given to the child component. However if we make a clone and change the clone's
            // nested property, then we can assign the clone to chartData which will trigger change detection.
            const result = normalizedAggregateResponseArray!.find(datum => {
                return value === DateTime.fromISO(datum.key!).toFormat(this.dateDisplayFormat);
            });
            const givenDateData = clone.find(datum => {
                return datum.name === DateTime.fromMillis(key).toFormat(this.dateDisplayFormat);
            });
            const countTypeData = givenDateData?.series.find(datum => datum.name === countType);
            if (countTypeData && result) {
                countTypeData.value = result.aggregate ?? 0;
            }
        });
        this.chartData = clone;
    }

    /**
     * The aggregates response array doesn't always have an item for each date to be displayed in the charts.
     * If this happens, then that particular count type (ex: numUpdated) won't be displayed correctly.
     * This method normalizes the array by adding any missing date entries to the aggregates response array.
     */
    private normalizeAggregateResponseArray(responses: AggregateResponse[]) {
        const normalizedAggregatesArray = [...responses];
        const dateEntriesToAdd = Array.from(this.chartRows.keys()).filter(dateEntry => {
            return !normalizedAggregatesArray.map(res => DateTime.fromISO(res.key).toMillis()).includes(dateEntry);
        });
        dateEntriesToAdd.forEach(millis =>
            normalizedAggregatesArray.push({ key: DateTime.fromMillis(millis).toISO()!, aggregate: 0 })
        );
        return normalizedAggregatesArray;
    }

    private setChartLabels() {
        for (let i = 0; i < this.timeRangeInDays; i++) {
            this.chartRows.set(
                this.today.minus({ days: i }).toMillis(),
                this.today.minus({ days: i }).toFormat(this.dateDisplayFormat)
            );
        }
    }

    private initChartData() {
        this.chartRows.forEach((value, key) => {
            this.chartData.push({
                name: DateTime.fromMillis(key).toFormat(this.dateDisplayFormat)!,
                series: [
                    { name: 'Created', value: 0 },
                    { name: 'Updated', value: 0 },
                    { name: 'Failed', value: 0 },
                    { name: 'Rejected', value: 0 },
                ],
            });
        });
        this.chartData = [...this.chartData];
    }

    viewDetails() {
        // get data to pass to dialog popup
        this.#dialog.open<ActionActivityDialogComponent, ActionActivityDialogData, any>(ActionActivityDialogComponent, {
            width: 'calc(100vw - 5px)',
            height: 'calc(100vh - 40px)',
            disableClose: false,
            restoreFocus: false,
            data: {
                selectedDivisionIds: this.selectedDivisionIds()
            }
        });
    }

    private loadCountsForLast24Hours(divisionIds: number[]) {

        const createdRecsRequests: ReportingSumRequest[] = [];
        divisionIds.forEach(id =>
            createdRecsRequests.push({
                field: 'numRecordsSuccessful',
                operation: 'sum',
                groupBy: 'startDate',
                groupByJoinAttribute: 'playResultEntity',
                groupBySecondsRange: this.oneDayInSeconds,
                groupBySecondsOffset: this.today.offset * 60,
                search: {
                    executionStartRangeBegin: DateTime.now().minus({ hours: 24 }).toISO(),
                    executionStartRangeEnd: DateTime.now().toISO(),
                    divisionId: id,
                    actionType: 'create',
                },
            })
        )

        const updatedRecsRequests: ReportingSumRequest[] = [];
        divisionIds.forEach(id =>
            updatedRecsRequests.push({
                field: 'numRecordsSuccessful',
                operation: 'sum',
                groupBy: 'startDate',
                groupByJoinAttribute: 'playResultEntity',
                groupBySecondsRange: this.oneDayInSeconds,
                groupBySecondsOffset: this.today.offset * 60,
                search: {
                    executionStartRangeBegin: DateTime.now().minus({ hours: 24 }).toISO(),
                    executionStartRangeEnd: DateTime.now().toISO(),
                    divisionId: id,
                    actionType: 'update',
                },
            })
        )

        this.#store.getCountsForLast24Hours({ requests: createdRecsRequests, countsType: 'numRecordsCreated'});
        this.#store.getCountsForLast24Hours({ requests: updatedRecsRequests, countsType: 'numRecordsUpdated'});
    }

    private loadSums(divisionIds: number[]) {

        const buildRequestsForSums = (countsType: CountsType, divisionId: number): ReportingSumRequest[] => {
            const baseRequest: ReportingSumRequest = {
                field: '',
                operation: 'sum',
                groupBy: 'startDate',
                groupByJoinAttribute: 'playResultEntity',
                groupBySecondsRange: this.oneDayInSeconds,
                groupBySecondsOffset: this.today.offset * 60,
                search: {
                    executionStartRangeBegin: this.today.minus({ days: this.timeRangeInDays - 1 }).toISO(),
                    executionStartRangeEnd: this.today.endOf('day').toISO(),
                    divisionId: divisionId,
                },
            };

            const createRequest: ReportingSumRequest = structuredClone(baseRequest);
            createRequest.search.actionType = 'create';
            const updateRequest: ReportingSumRequest = structuredClone(baseRequest);
            updateRequest.search.actionType = 'update';

            const requests: ReportingSumRequest[] = [];
            let field: keyof PlayResultDetail;
            switch (countsType) {
                case 'numRecordsCreated':
                    field = 'numRecordsSuccessful';
                    requests.push(createRequest);
                    createRequest.field = field;
                    break;
                case 'numRecordsUpdated':
                    field = 'numRecordsSuccessful';
                    requests.push(updateRequest);
                    updateRequest.field = field;
                    break;
                case 'numRecordsFailed':
                    field = 'numRecordsFailed';
                    requests.push(createRequest, updateRequest);
                    createRequest.field = field;
                    updateRequest.field = field;
                    break;
                case 'numRecordsRejected':
                    field = 'numRecordsRejected';
                    requests.push(createRequest, updateRequest);
                    createRequest.field = field;
                    updateRequest.field = field;
                    break;
            }
            return requests;
        };
        const typesToProcess = [
            'numRecordsCreated',
            'numRecordsFailed',
            'numRecordsRejected',
            'numRecordsUpdated',
        ] as CountsType[];

        typesToProcess.forEach(countsType => {
            let requests: ReportingSumRequest[] = divisionIds
                .map(id => buildRequestsForSums(countsType, id))
                .flat();
            this.#store.getAggregates({ requests, countsType })
        });
    }
}
